import {
  MAX_RERUN_FAILED_TEST_CASES,
  RERUN_DEFAULT_ITERATIONS,
  RERUN_FAILED_DEFAULT_ITERATIONS,
} from "../model/constants"
import type { ParseActionArgsResult, SofiaProposedAction } from "../model/types"
import { readString, readStringArray } from "./actionArgumentReaders"

const MAX_ITERATIONS = 1000

export function parseRunActionArgs(
  action: SofiaProposedAction,
): ParseActionArgsResult | undefined {
  const problems: string[] = []
  // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check -- This parser owns only run-control actions.
  switch (action.kind) {
    case "launch_run": {
      const test_set_id = readString(action.args, "test_set_id")
      const agent_id = readString(action.args, "agent_id")
      if (!test_set_id) {
        problems.push("missing test_set_id")
      }
      if (!agent_id) {
        problems.push("missing agent_id")
      }
      if (problems.length > 0 || !test_set_id || !agent_id) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "launch_run",
          args: {
            test_set_id,
            agent_id,
            persona_id: readString(action.args, "persona_id"),
            metric_ids: readStringArray(action.args, "metric_ids"),
            display_name: readString(action.args, "display_name"),
          },
        },
      }
    }
    case "rerun_run": {
      const run_id = readString(action.args, "run_id")
      if (!run_id) {
        return { ok: false, problems: ["missing run_id"] }
      }
      const rawIterations = action.args.iterations
      let iterations = RERUN_DEFAULT_ITERATIONS
      if (typeof rawIterations === "number" && Number.isFinite(rawIterations)) {
        iterations = Math.min(
          MAX_ITERATIONS,
          Math.max(1, Math.round(rawIterations)),
        )
      }
      return {
        ok: true,
        action: { kind: "rerun_run", args: { run_id, iterations } },
      }
    }
    case "rerun_failed": {
      const run_id = readString(action.args, "run_id")
      const test_case_ids = readStringArray(action.args, "test_case_ids")
      if (!run_id) {
        problems.push("missing run_id")
      }
      if (!test_case_ids || test_case_ids.length === 0) {
        problems.push("missing test_case_ids")
      } else if (test_case_ids.length > MAX_RERUN_FAILED_TEST_CASES) {
        // Mirrors sofia-agent's max_items: fail closed rather than sending a
        // list the backend will reject.
        problems.push(
          `too many test_case_ids (${test_case_ids.length} > ${MAX_RERUN_FAILED_TEST_CASES})`,
        )
      }
      if (problems.length > 0 || !run_id || !test_case_ids) {
        return { ok: false, problems }
      }
      const rawFailedIterations = action.args.iterations
      let iterations = RERUN_FAILED_DEFAULT_ITERATIONS
      if (
        typeof rawFailedIterations === "number" &&
        Number.isFinite(rawFailedIterations)
      ) {
        iterations = Math.min(
          MAX_ITERATIONS,
          Math.max(1, Math.round(rawFailedIterations)),
        )
      }
      return {
        ok: true,
        action: {
          kind: "rerun_failed",
          args: { run_id, test_case_ids, iterations },
        },
      }
    }
    case "cancel_run": {
      const run_id = readString(action.args, "run_id")
      if (!run_id) {
        return { ok: false, problems: ["missing run_id"] }
      }
      return { ok: true, action: { kind: "cancel_run", args: { run_id } } }
    }
    case "update_run": {
      const run_id = readString(action.args, "run_id")
      if (!run_id) {
        return { ok: false, problems: ["missing run_id"] }
      }
      // tags is the full replacement set and must be present; an empty list is
      // the explicit "clear all tags" case.
      const tags = readStringArray(action.args, "tags")
      if (tags === undefined) {
        return { ok: false, problems: ["missing tags"] }
      }
      return {
        ok: true,
        action: { kind: "update_run", args: { run_id, tags } },
      }
    }
    case "attach_metrics_to_agent": {
      const agent_id = readString(action.args, "agent_id")
      const metric_ids = readStringArray(action.args, "metric_ids")
      if (!agent_id) {
        problems.push("missing agent_id")
      }
      if (!metric_ids || metric_ids.length === 0) {
        problems.push("missing metric_ids")
      }
      if (problems.length > 0 || !agent_id || !metric_ids) {
        return { ok: false, problems }
      }
      return {
        ok: true,
        action: {
          kind: "attach_metrics_to_agent",
          args: { agent_id, metric_ids },
        },
      }
    }
    default:
      return undefined
  }
}
